/**
 * Date and value formatting helpers for chart tooltips and axis labels.
 * Uses moment-compatible format strings (not Highcharts % tokens).
 */
import {DataGranularity, HistoryMetric} from '../../api/types';
import * as Dates from '../Dates';
import {DateTimeConfig} from '../Dates';
import {isRainMetric} from '../Generic/RainMetricUtils';
import * as Units from '../Units';

/**
 * Returns a moment-compatible date format string for tooltip headers
 * based on the data granularity.
 */
export const getTooltipDateFormat = (
  granularity: DataGranularity,
  dateTimeConfig?: DateTimeConfig,
): string => {
  switch (granularity) {
    case 'max':
    case 'H':
      if (dateTimeConfig && !dateTimeConfig.is_24_hour) {
        return 'h:mm A (dddd D MMMM YYYY)';
      }
      return 'HH:mm (dddd D MMMM YYYY)';
    case '3H':
    case '6H':
      return 'dddd D MMMM YYYY HH:mm';
    case 'D':
      return 'dddd D MMMM YYYY';
    case 'W':
      return 'D MMMM YYYY';
    case 'M':
      return 'MMMM YYYY';
    default:
      return 'lll';
  }
};

/**
 * Returns a moment-compatible date format for x-axis tick labels.
 */
export const getAxisDateFormat = (granularity: DataGranularity): string => {
  switch (granularity) {
    case 'max':
    case 'H':
      return 'HH:mm';
    case '3H':
    case '6H':
      return 'HH:mm';
    case 'D':
      return 'D MMM';
    case 'W':
      return 'D MMM';
    case 'M':
      return 'MMM';
    default:
      return 'lll';
  }
};

/**
 * Format a unix timestamp (seconds) into a human-readable string.
 */
export const formatTimestamp = (
  timestamp: number,
  timezone: string,
  format: string,
): string => {
  return Dates.formatDate(timestamp, timezone, format);
};

/**
 * Get the unit label string for a given metric.
 */
export const getMetricUnitLabel = (
  metric: string,
  units: Units.Units,
): string => {
  return Units.getUnitLabel(units, metric);
};

/**
 * Convert a raw metric value using the user's preferred units.
 */
export const convertMetricValue = (
  value: number | null | undefined,
  metric: string,
  units: Units.Units,
  precision?: number,
): number | null => {
  if (value === null || value === undefined) return null;
  return Units.convertValue(value, metric, units, precision);
};

/**
 * Returns the default decimal precision for a given metric type.
 * This matches the existing ChartUtils.getDataTypePrecision.
 */
export const getMetricPrecision = (
  metric: string,
  units?: Units.Units,
): number => {
  // When units are provided, use unit-aware precision from the mapping
  if (units) {
    const unitPrecision = Units.getPrecision(metric, units);
    if (unitPrecision !== null) return unitPrecision;
  }

  // Fallback: static precision when units are not available
  switch (metric) {
    case 'T':
      return 1;
    case 'U':
      return 0;
    case 'SLP':
      return 1;
    case 'RR_1H':
    case 'RR_3H':
    case 'RR_6H':
    case 'RR_12H':
    case 'RR_24H':
      return 1;
    case 'SOLAR_RADIATION':
      return 0;
    case 'UV':
      return 1;
    case 'VIS':
      return 1;
    case 'FF':
    case 'FXY':
      return 0;
    case 'AQI':
      return 0;
    case 'LIGHTNING_STRIKE_COUNT':
      return 0;
    default:
      return 0;
  }
};

/**
 * Format a numeric value with the appropriate precision for its metric.
 */
export const formatMetricValue = (
  value: number,
  metric: string,
  units: Units.Units,
  precision?: number,
): string => {
  const converted = Units.convertValue(value, metric, units, precision);
  if (converted === null) return '-';
  const p = precision ?? getMetricPrecision(metric, units);
  return converted.toFixed(p);
};

/**
 * Maps a HistoryMetric to the base metric type used for unit conversion.
 * E.g. T_MIN, T_MAX -> 'T' for temperature conversion.
 */
export const getConversionMetric = (metric: HistoryMetric): string => {
  if (metric.startsWith('T_') || metric === 'T') return 'T';
  if (
    metric === 'HEAT_INDEX' ||
    metric === 'HUMIDEX' ||
    metric === 'WIND_CHILL' ||
    metric === 'THSW' ||
    metric === 'THW'
  )
    return 'T';
  if (metric.startsWith('U_') || metric === 'U') return 'U';
  if (metric.startsWith('SLP_') || metric === 'SLP') return 'SLP';
  if (isRainMetric(metric) || metric === 'ET') return 'RR_1H';
  if (metric === 'DD' || metric === 'DXY') return metric; // direction angles, no conversion
  if (metric === 'FF' || metric === 'FXY') return 'FF';
  if (metric.startsWith('SOLAR_RADIATION') || metric === 'SOLAR_RADIATION')
    return 'SOLAR_RADIATION';
  if (metric === 'SOLAR_ENERGY') return 'SOLAR_ENERGY';
  if (metric.startsWith('UV') || metric === 'UV') return 'UV';
  if (metric.startsWith('VIS') || metric === 'VIS') return 'VIS';
  if (metric.startsWith('AQI') || metric === 'AQI') return 'AQI';
  if (metric === 'WIND_RUN') return 'WIND_RUN';
  return metric;
};
